import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import { useTheme } from '@mui/material/styles'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Grid from '@mui/material/Grid2'
import { useTranslation } from 'react-i18next'
import { ILeisureActivity } from 'types/cvsInterfaces'
import { IItemVisibility, ILayout } from 'types/layoutInterfaces'
import { chooseDBTranslation } from 'utils/translations'
import VisibilityCheckbox from '../VisibilityCheckbox'
import HideDetailsSettings from './HideDetailsSettings'
import { findHideSetting, hasHiddenDetails } from '../hideSettings'
import colors from 'constants/colors'
import { convertToDisplayDate } from 'utils/utils'

interface ILeisureActivitiesProps {
  leisureActivities: ILeisureActivity[]
  layout: ILayout
  hideSection: (value: boolean, type: string) => void
  hideItem: (value: boolean, id: number, type: string) => void
  expanded: boolean
  setStatus: (value: boolean) => void
  handleHideSettings: (data: Record<string, boolean>, type: string) => void
}

const LeisureActivities: React.FC<ILeisureActivitiesProps> = ({
  leisureActivities,
  layout,
  hideSection,
  hideItem,
  expanded,
  setStatus,
  handleHideSettings,
}) => {
  const { t, i18n } = useTranslation()
  const theme = useTheme()
  const hideSetting = findHideSetting(layout, 'leisureActivities')
  const activityItems = leisureActivities ?? []

  let hidden = false
  if (layout && layout.ItemVisibilities && layout.ItemVisibilities.length > 0) {
    const visibilities = layout.ItemVisibilities.filter(
      (visibility: IItemVisibility) => visibility.type === 'leisureActivities'
    )
    if (visibilities.length > 0) {
      const visibilityMap = new Map(visibilities.map((item: IItemVisibility) => [item.itemId, item.hidden]))
      hidden = activityItems.every((value: ILeisureActivity) => visibilityMap.get(value.id) === true)
    }
  }

  const Item: React.FC<{ leisureActivity: ILeisureActivity }> = ({ leisureActivity }) => {
    let hiddenItem = false
    if (layout && layout.ItemVisibilities && layout.ItemVisibilities.length > 0) {
      const visibilities = layout.ItemVisibilities.filter(
        (visibility: IItemVisibility) => visibility.type === 'leisureActivities'
      )
      if (visibilities.length > 0) {
        hiddenItem = visibilities.some(
          (value: IItemVisibility) => value.itemId === leisureActivity.id && value.hidden === true
        )
      }
    }

    const tr = chooseDBTranslation(i18n, leisureActivity)
    const dateText = `${
      leisureActivity.startDate ? convertToDisplayDate(leisureActivity.startDate, leisureActivity.dateResolution) : ''
    }${leisureActivity.startDate || leisureActivity.endDate ? ' - ' : ''}${
      leisureActivity.endDate ? convertToDisplayDate(leisureActivity.endDate, leisureActivity.dateResolution) : ''
    }`

    return (
      <Box
        display="flex"
        flexDirection="row"
        flexWrap="nowrap"
        width="100%"
        my={2}
        sx={{
          padding: '2px',
          backgroundColor: theme.palette.background.default,
          '&:hover': {
            backgroundColor: theme.palette.background.paper,
            borderRadius: '5px',
          },
        }}
      >
        <Box width="100%">
          <Box display="flex" flexDirection="row" flexWrap="nowrap" width="100%" mb={1}>
            <VisibilityCheckbox
              checked={!!hiddenItem}
              onChange={(checked: boolean) => hideItem(checked, leisureActivity.id, 'leisureActivities')}
            />
            <Box display="inline" fontWeight="fontWeightBold">
              {tr?.name}
            </Box>
          </Box>

          {!!tr?.activityType && (
            <Box display="flex" flexDirection="row" flexWrap="nowrap" width="100%" mb={1}>
              <Box mr={1} fontWeight="fontWeightBold">
                {t('print.leisureActivities.type')}:
              </Box>
              <Box display="inline">{tr.activityType}</Box>
            </Box>
          )}

          {!!dateText.trim() && (
            <Box display="flex" flexDirection="row" flexWrap="nowrap" width="100%" mb={1}>
              <Box mr={1} fontWeight="fontWeightBold">
                {t('print.leisureActivities.date')}:
              </Box>
              <Box display="inline">{dateText}</Box>
            </Box>
          )}

          <Box>{tr?.description}</Box>
        </Box>
      </Box>
    )
  }

  return (
    <Accordion
      expanded={expanded}
      onChange={(_e, expanded) => setStatus(expanded)}
      elevation={0}
      sx={{ borderRadius: '5px', border: `1px solid ${colors.borderColor}` }}
      slotProps={{
        transition: { unmountOnExit: true },
      }}
    >
      <Box display="flex" alignItems="center">
        <VisibilityCheckbox
          checked={hidden}
          onChange={(checked: boolean) => hideSection(checked, 'leisureActivities')}
        />
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            flex: 1,
            fontSize: '20px',
            color: theme.palette.primary.main,
            fontWeight: 'bold',
          }}
        >
          <Grid container alignItems="center">
            <Grid>{t('print.layout.leisureActivities')}</Grid>
            <Grid sx={{ marginLeft: 1, fontSize: '11pt', color: theme.palette.text.secondary, fontWeight: 'normal' }}>
              {hasHiddenDetails(hideSetting, 'leisureActivities')
                ? t('print.hideSetting.enabled')
                : t('print.hideSetting.disabled')}
            </Grid>
          </Grid>
        </AccordionSummary>
      </Box>
      <AccordionDetails>
        <HideDetailsSettings type="leisureActivities" layout={layout} handleHideSettings={handleHideSettings} />
        <Box display="flex" flexDirection="column" flexWrap="wrap" width="100%">
          {activityItems.map((leisureActivity: ILeisureActivity, index: number) => {
            return (
              <Box key={`leisureActivity-${index}`}>
                <Item leisureActivity={leisureActivity} />
                {index + 1 < activityItems.length ? <Divider variant="fullWidth" /> : null}
              </Box>
            )
          })}
        </Box>
      </AccordionDetails>
    </Accordion>
  )
}

export default LeisureActivities
